import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Upload, 
  CheckCircle, 
  AlertCircle, 
  TrendingUp, 
  TrendingDown,
  Lightbulb,
  Target,
  Award,
  X,
  Loader,
  FileCheck
} from 'lucide-react';
import { analyzeResume, analyzeResumeFile } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';

const ResumeAnalyzer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [analysisMode, setAnalysisMode] = useState('builder'); // 'builder' or 'upload'
  const [resumeData, setResumeData] = useState(location.state?.resumeData || null);
  const [jobTitle, setJobTitle] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  const handleAnalyzeFromBuilder = async () => {
    if (!resumeData) {
      toast.error('No resume data found. Please create a resume first.');
      navigate('/resume-builder');
      return;
    }

    setIsAnalyzing(true);
    try {
      const result = await analyzeResume(resumeData, jobTitle || null, jobDescription || null);
      setAnalysisResult(result);
      toast.success('Resume analyzed successfully!');
    } catch (error) {
      console.error('Error analyzing resume:', error);
      toast.error('Failed to analyze resume. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      toast.success('File ready for analysis');
    }
  };

  const handleAnalyzeUploaded = async () => {
    if (!uploadedFile) {
      toast.error('Please upload a resume file first.');
      return;
    }

    setIsAnalyzing(true);
    try {
      const result = await analyzeResumeFile(uploadedFile, jobTitle || null, jobDescription || null);
      setAnalysisResult(result);
      toast.success('Uploaded resume analyzed successfully!');
    } catch (error) {
      console.error('Error analyzing uploaded resume:', error);
      toast.error('Failed to analyze uploaded resume. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getRatingColor = (rating) => {
    switch (rating) {
      case 'Over-Qualified':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'Qualified':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'Under-Qualified':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Weak':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getRatingIcon = (rating) => {
    switch (rating) {
      case 'Over-Qualified':
        return <TrendingUp className="w-5 h-5" />;
      case 'Qualified':
        return <CheckCircle className="w-5 h-5" />;
      case 'Under-Qualified':
        return <AlertCircle className="w-5 h-5" />;
      case 'Weak':
        return <TrendingDown className="w-5 h-5" />;
      default:
        return <FileCheck className="w-5 h-5" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Low':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Resume Analyzer</h1>
          <p className="text-lg text-gray-600">
            Get AI-powered feedback on your resume with ratings and actionable suggestions
          </p>
        </div>

        {!analysisResult ? (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            {/* Mode Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Analysis Mode
              </label>
              <div className="flex gap-4">
                <button
                  onClick={() => setAnalysisMode('builder')}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                    analysisMode === 'builder'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-primary-300'
                  }`}
                >
                  <FileText className="w-5 h-5 inline-block mr-2" />
                  Use Resume Builder Data
                </button>
                <button
                  onClick={() => setAnalysisMode('upload')}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                    analysisMode === 'upload'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-primary-300'
                  }`}
                >
                  <Upload className="w-5 h-5 inline-block mr-2" />
                  Upload Resume File
                </button>
              </div>
            </div>

            {/* Analysis Form */}
            {analysisMode === 'builder' ? (
              <div className="space-y-4">
                {!resumeData ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <p className="text-yellow-800 mb-3">
                      No resume data found. Please create a resume first.
                    </p>
                    <button
                      onClick={() => navigate('/resume-builder')}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Go to Resume Builder
                    </button>
                  </div>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <p className="text-green-800 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Resume data loaded from builder
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Job Title (Optional)
                  </label>
                  <input
                    type="text"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="e.g., Software Engineer"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Description (Optional)
                  </label>
                  <textarea
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder="Paste the job description here for targeted analysis..."
                    rows="5"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <button
                  onClick={handleAnalyzeFromBuilder}
                  disabled={!resumeData || isAnalyzing}
                  className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                      Analyzing Resume...
                    </>
                  ) : (
                    <>
                      <Target className="w-5 h-5 mr-2" />
                      Analyze Resume
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Resume (PDF or Word)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="resume-upload"
                    />
                    <label
                      htmlFor="resume-upload"
                      className="cursor-pointer bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors inline-block"
                    >
                      Choose File
                    </label>
                    {uploadedFile && (
                      <p className="mt-2 text-sm text-gray-600">{uploadedFile.name}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Job Title (Optional)
                  </label>
                  <input
                    type="text"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="e.g., Software Engineer"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Description (Optional)
                  </label>
                  <textarea
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder="Paste the job description here for targeted analysis..."
                    rows="5"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <button
                  onClick={handleAnalyzeUploaded}
                  disabled={!uploadedFile || isAnalyzing}
                  className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                      Analyzing Uploaded Resume...
                    </>
                  ) : (
                    <>
                      <Target className="w-5 h-5 mr-2" />
                      Analyze Uploaded Resume
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Rating Card */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Analysis Results</h2>
                <button
                  onClick={() => {
                    setAnalysisResult(null);
                    setJobTitle('');
                    setJobDescription('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Rating */}
                <div className={`border-2 rounded-lg p-6 ${getRatingColor(analysisResult.rating)}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Overall Rating</h3>
                    {getRatingIcon(analysisResult.rating)}
                  </div>
                  <div className="text-3xl font-bold mb-2">{analysisResult.rating}</div>
                  <div className="text-sm opacity-75">
                    Score: {analysisResult.overall_score}/100
                  </div>
                </div>

                {/* ATS Compatibility */}
                {analysisResult.ats_compatibility && (
                  <div className="border-2 border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">ATS Compatibility</h3>
                      <FileCheck className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="text-3xl font-bold mb-2 text-gray-900">
                      {analysisResult.ats_compatibility.score}/100
                    </div>
                    <div className="text-sm text-gray-600">
                      {analysisResult.ats_compatibility.feedback}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <FileText className="w-6 h-6 mr-2 text-primary-600" />
                Summary
              </h3>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {analysisResult.summary}
              </p>
            </div>

            {/* Strengths and Weaknesses */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-green-50 rounded-lg shadow-lg p-6 border border-green-200">
                <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center">
                  <Award className="w-6 h-6 mr-2" />
                  Strengths
                </h3>
                <ul className="space-y-2">
                  {analysisResult.strengths?.map((strength, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-green-800">{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-red-50 rounded-lg shadow-lg p-6 border border-red-200">
                <h3 className="text-xl font-bold text-red-900 mb-4 flex items-center">
                  <AlertCircle className="w-6 h-6 mr-2" />
                  Areas for Improvement
                </h3>
                <ul className="space-y-2">
                  {analysisResult.weaknesses?.map((weakness, index) => (
                    <li key={index} className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-red-800">{weakness}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Suggestions */}
            {analysisResult.suggestions && analysisResult.suggestions.length > 0 && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Lightbulb className="w-6 h-6 mr-2 text-yellow-500" />
                  Actionable Suggestions
                </h3>
                <div className="space-y-4">
                  {analysisResult.suggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="border-l-4 border-primary-500 pl-4 py-2"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-900">
                          {suggestion.category}
                        </span>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(
                            suggestion.priority
                          )}`}
                        >
                          {suggestion.priority} Priority
                        </span>
                      </div>
                      <p className="text-gray-700">{suggestion.suggestion}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section Analysis */}
            {analysisResult.section_analysis && Object.keys(analysisResult.section_analysis).length > 0 && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Section-by-Section Analysis</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.entries(analysisResult.section_analysis).map(([section, data]) => (
                    <div key={section} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 capitalize">
                          {section.replace('_', ' ')}
                        </h4>
                        <span className="text-lg font-bold text-primary-600">{data.score}/100</span>
                      </div>
                      <p className="text-sm text-gray-600">{data.feedback}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Keyword Analysis */}
            {analysisResult.keyword_analysis && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Keyword Analysis</h3>
                {analysisResult.keyword_analysis.missing_keywords &&
                  analysisResult.keyword_analysis.missing_keywords.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-700 mb-2">Missing Keywords:</h4>
                      <div className="flex flex-wrap gap-2">
                        {analysisResult.keyword_analysis.missing_keywords.map((keyword, index) => (
                          <span
                            key={index}
                            className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                {analysisResult.keyword_analysis.recommendations && (
                  <p className="text-gray-700">{analysisResult.keyword_analysis.recommendations}</p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setAnalysisResult(null);
                  setJobTitle('');
                  setJobDescription('');
                }}
                className="flex-1 bg-gray-200 text-gray-800 py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Analyze Another Resume
              </button>
              <button
                onClick={() => navigate('/resume-builder', { state: { resumeData } })}
                className="flex-1 bg-primary-600 text-white py-3 px-6 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Edit Resume
              </button>
            </div>
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ResumeAnalyzer;

